import * as zod from 'zod';

import { MotionSpeed, SpeedLimitOption, Step } from '@sb/remote-control/types';
import { calculateSpeedProfileFromStepMotionSpeed } from '@sb/remote-control/util/calculateSpeedProfileFromStepMotionSpeed';
import type { StepKind, SpeedProfile } from '@sb/routine-runner';
import { getRotaryWeldPermittedChildStepKindsShared } from '@sb/routine-validation/step-rules/weld';

import {
  GasTypeRaw,
  WireSizeRaw,
  WireTypeRaw,
} from '../../../MillerWeldMachine/implementation/constants';
import {
  MILLER_DEFAULTS,
  MillerWeldParameters,
} from '../../../MillerWeldMachine/types/MillerWeldMachineCommands';
import { RotaryWeldCompletion } from '../../steps/RotaryWeld/Arguments';
import { DeviceKinds } from '../../types/DeviceKinds';

/** The waypoint that fixes the torch. A rotary weld takes exactly one. */
export const ROTARY_WELD_WAYPOINT_STEP_KIND = 'Waypoint' as const;

/**
 * Default rotation time for a newly added step, in seconds. The PRD specifies
 * only the field's bounds (0.1 increments, 500s max); 5s is the product call.
 */
export const ROTARY_WELD_DEFAULT_DURATION_SECONDS = 5;

export namespace RotaryWeldStepDatabase {
  export const name = 'Rotary weld';

  export const description =
    'Weld a round part while a positioner rotates it under a fixed torch';

  /**
   * Same welders as the Weld step: the arc hardware is identical, only what
   * moves during the weld differs. The positioner is selected inside the step
   * rather than gating its availability, so it is deliberately not listed —
   * a robot with a welder should offer the step and then say what is missing.
   */
  export const deviceKinds = DeviceKinds;

  export const isDecorator = true;

  export const librarySection = Step.LibrarySection.InterfaceWithMachines;

  /** Sorts immediately after Weld ('2'), which is where the PRD groups it. */
  export const librarySort = '2.1';

  export const argumentKind = 'RotaryWeld';

  export const featureFlag = 'rotaryWeldStep' as const;

  /**
   * A waypoint and nothing else. The Weld step permits `Arc` children and more
   * than one target because robot motion defines its path; here the robot holds
   * still, so a second target would describe a weld this step cannot perform.
   * The count is enforced by the validation rule — this only bounds the kinds.
   */
  export function getPermittedChildStepKinds(): readonly StepKind[] {
    return getRotaryWeldPermittedChildStepKindsShared();
  }

  export const decoratorStepKind = ROTARY_WELD_WAYPOINT_STEP_KIND;

  /**
   * False, unlike Weld: the placeholder is an "Add step" row under the
   * decorator, and with `Waypoint` as the only permitted child it is a second
   * route to a second waypoint. Leaving it off means it shows only while the
   * step has no waypoint at all, which matches the wizard disabling its own
   * add button once one exists.
   */
  export const alwaysShowDecoratorStepPlaceholder = false;

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    /** Optional for the same backwards-compatibility reason as Weld's. */
    selectedMachineID: zod.string().optional(),
    selectedTorchID: zod.string().optional(),
    /** The `WeldPositioner` equipment that turns the part. */
    selectedPositionerID: zod.string().optional(),
    millerWeldParameters: MillerWeldParameters.default({
      mode: 'constantVoltage',
      voltage: MILLER_DEFAULTS.voltage,
      arcLength: MILLER_DEFAULTS.arcLength,
      arcControl: MILLER_DEFAULTS.arcControl,
      wireFeedSpeed: MILLER_DEFAULTS.wireFeedSpeed,
      wireType: WireTypeRaw.Aluminum_4943,
      wireSize: WireSizeRaw.Size_0_030,
      gasType: GasTypeRaw.Argon_100,
    }),
    /** Milliseconds, as in the Weld step; the fields edit seconds. */
    arcStartTime: zod.number().gte(0).default(0),
    craterFillTime: zod.number().gte(0).default(0),
    approachSpeed: zod.preprocess(
      (val) => (val == null ? undefined : val),
      MotionSpeed.optional(),
    ),
    approachSpeedOption: SpeedLimitOption.exclude(['PARENT_DEFAULTS']).default(
      'NO_MOTION_LIMITS',
    ),
    /**
     * PRD "Wait for". Defaulted so a newly added step parses — the runner
     * schema deliberately has no default, since a stored routine must say.
     */
    completion: RotaryWeldCompletion.default({
      kind: 'time',
      durationSeconds: ROTARY_WELD_DEFAULT_DURATION_SECONDS,
    }),
    /** PRD "Weld when aligned". */
    alignmentRequired: zod.boolean().default(false),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  /**
   * Travel speed and tooltip acceleration have no analog: nothing moves along
   * the joint while the arc is live, so the only motion this step plans is the
   * approach.
   */
  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    baseSpeedProfile,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const approachSpeedProfile: SpeedProfile =
      calculateSpeedProfileFromStepMotionSpeed({
        motionSpeed: args.approachSpeed,
        baseSpeedProfile,
      });

    return {
      ...stepData,
      stepKind: 'RotaryWeld',
      args: {
        ...args,
        approachSpeedProfile,
      },
    };
  };

  /**
   * "Rotary weld for 12s" / "Rotary weld until the positioner signals". What
   * ends the weld is the one thing a reader cannot infer from the step's name,
   * and unlike Weld's segment count it is a plain argument read — no routine
   * walk needed.
   */
  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration,
    includeStepName,
  }) => {
    if (stepConfiguration?.stepKind !== argumentKind) return null;

    const { args } = stepConfiguration;

    if (args?.argumentKind !== argumentKind) return null;

    const ending =
      args.completion.kind === 'time'
        ? `for ${args.completion.durationSeconds}s`
        : 'until the positioner signals';

    return [includeStepName ? name : false, ending]
      .filter((part) => part !== false)
      .join(' ');
  };
}

RotaryWeldStepDatabase satisfies Step.StepKindInfo;
